import { Component, Inject, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { ErrorStrings, ValidationPatterns } from '../../constants/form.constants';
import { Pet, PetGender, PetRehomingRequest } from '../../interfaces/pet';
import { Alert } from '../../interfaces/alert';
import {
  TokRequest,
  User,
  UserAddress,
  UserAddressType
} from '../../interfaces/user';
import { Species } from '../../interfaces/species';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import { PetService } from '../../services/pet/pet.service';
import { UserService } from '../../services/user/user.service';
import { HttpErrorResponse } from '@angular/common/http';
import { merge, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { TokOwnerDetail } from '../../interfaces/Account/ownerDetail';
import { OwnerAddress } from '../../interfaces/Account/ownerAddress';
import { AddressPAF } from '../../services/address-finder/address-finder.service';
import { UpdateOwnerDetailDTO } from '../../interfaces/Account/updateOwnerDetailDTO';
import { FormBuilder, FormControl, FormGroup, NgForm } from '@angular/forms';
import { atLeastOneCheckboxToBeCheckedValidator } from '../../shared/utils/utils';
import { TOK_DETAILS, TOK_PAYMENT } from 'src/app/constants/route.constants';
import { AddressService } from 'src/app/services/address/address.service';
import { isPlatformBrowser } from '@angular/common';
import { ConfigService } from 'src/app/services/config/config.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';
import { PerkService } from 'src/app/services/perk/perk.service';
import { environment } from 'src/environments/environment';
import { PetplanService } from 'src/app/services/petplan/petplan.service';
import { RegistrationFlowService } from 'src/app/services/registration-flow/registration-flow.service';
import { RegistrationFlowStep } from 'src/app/constants/registration-flow.constants';
import { errorMessagesMap } from 'src/app/constants/error-messages';

@Component({
  selector: 'app-tok-details',
  templateUrl: './tok-details.component.html',
  styleUrls: ['./tok-details.component.scss'],
})
export class TokDetailsComponent implements OnInit {
  user: User;

  @ViewChild('petBreedTypeahead') petBreedTypeahead: NgbTypeahead;
  @ViewChild('editPetForm') editPetForm: NgForm;

  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  alert: Alert = null;
  errorStrings = ErrorStrings;
  loadingSpecies = false;
  loadingBreeds = false;
  speciesList: Species[] = [];
  selectedSpecies: Species = null;
  breedNamesList: string[] = [];
  companyPhoneNumber: string;

  pet: PetRehomingRequest = {
    name: null,
    species: null,
    breed: null,
    gender: null,
    chipNumber: null,
    extraInfo: null,
    vetCode: null,
    existingKeeper: {
      name: null,
      phoneNumber: null,
    },
  };
  petForRehome: Pet = null;
  petGender = PetGender;
  validationPatterns = ValidationPatterns;
  tokRequests: TokRequest[];
  loading = false;
  isBrowser: boolean;
  petPlanText1: string;
  isPetPlanCheckboxChecked: boolean;
  petPlanCheckboxLabel: string;
  validationErrorMessage: string;
  petPlanText2: string;
  showPetPlanTextBox: boolean;
  petId: number;
  brandName: string;

  public communicationForm: FormGroup;
  public thirdPartyCheckboxStateChanged = false;
  public keeperDetailModel: TokOwnerDetail = {
    firstName: '',
    lastName: '',
    mainPhoneNumber: '',
    secondaryPhoneNumber: '',
  };

  public addressModel: OwnerAddress = {
    addressLine1: '',
    addressLine2: '',
    townPerCity: '',
    postcode: '',
    phoneNumber: '',
    contactName: '',
  };

  public postcodeCheck = null;
  public selectedAddress = null;
  public addressesSelectList = [];
  public isPostcodeInvalid = false;
  private _postcodeCheckChanged: Subject<string> = new Subject<string>();

  private _existingTelephones: { mainPhoneNumber: string, secondaryPhoneNumber: string };
  private _mainAddress: UserAddress = null;
  private _subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private petService: PetService,
    private userService: UserService,
    private _addressService: AddressService,
    private _fb: FormBuilder,
    private configService: ConfigService,
    private featureFlagService: FeatureFlagsService,
    private perkService: PerkService,
    private petplanService: PetplanService,
    private registrationFlowService: RegistrationFlowService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.user = this.route.snapshot.data.user as User;
    this.tokRequests = this.user.tokRequests as TokRequest[];
    this.companyPhoneNumber = this.configService.getTextFromKey('company-phone-number');
    this.petPlanText1 = this.configService.getTextFromKey('pet-details-page-petplan-text-1');
    this.petPlanCheckboxLabel = this.configService.getTextFromKey('pet-details-page-petplan-checkbox-label');
    this.petPlanText2 = this.configService.getTextFromKey('pet-details-page-petplan-text-2');
    this.brandName = this.configService.get('displayName');

    if (this.tokRequests.length) {
      this.petForRehome = this.petService.getTokRequestPet(this.tokRequests, this.user.pets)
      Object.keys(this.pet).forEach(key => {
        if (this.petForRehome[key] !== undefined) {
          this.pet[key] = this.petForRehome[key];
        }
      });
    }
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.redirectToDashboard();
  }

  ngOnInit(): void {
    this.initSubs();
    this.initForm();
    this.loadSpecies();
    this.initCommunicationForm();
    if (this.featureFlagService.isEnabled('isPetPlanFreeInsuranceAvailable')) {
      this.perkService
        .getPetEligibilityFromChipNumber(Number(this.pet.chipNumber), environment.eligibilityShortCode)
        .subscribe((res) => {
          this.showPetPlanTextBox = res.isEligible;
          this.petId = res.petId;
      });
    }
  }

  public updateOwnerDetailsAndAddresses() {
    this.loading = true;

    const ownerData: UpdateOwnerDetailDTO = {
      title: '',
      dateOfBirth: new Date(),
      gender: '',
      firstName: this.keeperDetailModel.firstName,
      lastName: this.keeperDetailModel.lastName,
      mainPhoneNumber: this.keeperDetailModel.mainPhoneNumber || '',
      secondaryPhoneNumber: this.keeperDetailModel.secondaryPhoneNumber || '',
    };

    const ownerAddressDTO = [
      {
        line1: this.addressModel.addressLine1,
        line2: this.addressModel.addressLine2,
        town: this.addressModel.townPerCity,
        county: this._mainAddress?.county ? this._mainAddress.county : null,
        postcode: this.addressModel.postcode,
        addressType: UserAddressType.Main,
        phoneNumber: this.addressModel.phoneNumber,
        contactName: this.addressModel.contactName,
        temporary: this._mainAddress?.temporary ? this._mainAddress.temporary : null,
      },
    ];

    this.userService
      .updateOwnerDetailsAndAddresses(ownerData, ownerAddressDTO)
      .subscribe(
        (response) => {
          if (this.featureFlagService.isEnabled('isFreePetInsuranceCheckBoxAvailable') && this.isPetPlanCheckboxChecked) {
            this.petplanService.petplanSave(this.petId).subscribe();
          }
          // B21K has one merged step for this page, other TOK requests use the pet
          // details one. completeStep skips the step that isn't in the user's flow.
          this.registrationFlowService
            .completeStep(RegistrationFlowStep.UserAndPetDetails)
            .pipe(
              switchMap(() =>
                this.registrationFlowService.completeStep(RegistrationFlowStep.PetDetails)
              )
            )
            .subscribe(() => {
              // completeStep never errors: a B21K keeper still on this page's step
              // means it failed, and the payment page would only send them back here.
              if (this.registrationFlowService.getB21KRoute() === `/${TOK_DETAILS}`) {
                this.alert = { type: 'danger', message: errorMessagesMap.generic_error_message };
                return;
              }
              this.navigateToPayment();
            })
            .add(() => (this.loading = false));
          this.getExistingTelephones();
          // Tracking moved here as the dedicated email sending API has been discontinued, 
          // but we still need to record the submission event for analytics purposes.
          this.trackEvent('petrehomeresupportemailquest::submitted');
          this.alert = {
            type: 'success',
            message: "Your detail information was updated. You'll get redirected in a few seconds!",
          };
        },
        (err) => {
          this.loading = false;
          if (err instanceof HttpErrorResponse && err.status === 400) {
            this.alert = {
              type: 'danger',
              message: 'Please enter valid phone numbers.',
            };
          } else {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
        }
      );
  }

  private getExistingTelephones(): { mainPhoneNumber: string, secondaryPhoneNumber: string } {
    return {
      mainPhoneNumber: this.user.contact.mainPhoneNumber || '',
      secondaryPhoneNumber: this.user.contact.secondaryPhoneNumber || ''
    }
  }

  private initForm() {
    this._existingTelephones = this.getExistingTelephones();
    this.keeperDetailModel = {
      firstName: this.user.name.firstName,
      lastName: this.user.name.lastName,
      mainPhoneNumber: this._existingTelephones.mainPhoneNumber,
      secondaryPhoneNumber: this._existingTelephones.secondaryPhoneNumber
    };

    if (this._mainAddress) {
      this.addressModel = {
        addressLine1: this._mainAddress.line1,
        addressLine2: this._mainAddress.line2,
        townPerCity: this._mainAddress.town,
        postcode: this._mainAddress.postcode,
      };
    }
  }

  save(): void {
    this.closeAlert();

    if (this.editPetForm.invalid) {
      Object.keys(this.editPetForm.controls).forEach(key => {
        this.editPetForm.controls[key].markAsTouched();
      });
      return;
    }

    // Navigates only after the save and the steps succeed, so a failed save isn't hidden.
    this.updateOwnerDetailsAndAddresses();
  }

  private navigateToPayment(): void {
    this.router.navigate([`/${TOK_PAYMENT}`], { queryParams: { petName: this.pet.name } });
  }

  closeAlert() {
    this.alert = null;
  }

  updateBreedsList(): void {
    // Loading breeds list
    this.breedNamesList = [];
    this.loadingBreeds = true;
    this.pet.species = this.selectedSpecies.description;
    this.petService
      .getBreedsFor(this.selectedSpecies.venomCode)
      .subscribe(
        (breedsList) => {
          this.breedNamesList = Object.keys(breedsList);

          if (this.breedNamesList.length === 1) {
            this.pet.breed = this.breedNamesList[0];
          } else {
            this.pet.breed = null;
          }
        },
        (err) => {
          this.alert = {
            type: 'danger',
            message:
              'An error has occurred while retrieving some information. Please try again later.',
          };
        }
      )
      .add(() => {
        this.loadingBreeds = false;
      });
  }

  searchBreed = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(
      filter(() => !this.petBreedTypeahead.isPopupOpen())
    );
    const inputFocus$ = this.focus$;
    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      map((term) => {
        if (term === '') {
          return this.breedNamesList.length <= 10 ? this.breedNamesList : [];
        } else {
          return this.breedNamesList
            .filter((v) => v.toLowerCase().indexOf(term.toLowerCase()) > -1)
            .slice(0, 10);
        }
      })
    );
  };

  private loadSpecies(): void {
    // Loading species list
    this.loadingSpecies = true;
    this.petService
      .getAllSpecies()
      .subscribe(
        (speciesList) => {
          this.speciesList = speciesList;
          this.selectedSpecies = this.speciesList.find(s => s.description === this.pet.species)!;
        },
        (err) => {
          this.alert = {
            type: 'danger',
            message:
              'An error has occurred while retrieving some information. Please try again later.',
          };
        }
      )
      .add(() => {
        this.loadingSpecies = false;
      });
  }

  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  public initCommunicationForm() {
    this.communicationForm = this._fb.group({
      subscribeToAllInternalCommunication: [false],
      thirdPartySubscribeOptionSelectGroup: new FormGroup(
        {
          subscribeToAllThirdParty: new FormControl(false),
          unsubscribeFromAllThirdParty: new FormControl(false),
        },
        atLeastOneCheckboxToBeCheckedValidator()
      ),
    });
  }

  public onSubscribeOptionChanged(event) {
    this.thirdPartyCheckboxStateChanged = true;
    if (event.target.checked) {
      if (event.target.id == 'subscribeToAllThirdParty') {
        this.communicationForm.controls['thirdPartySubscribeOptionSelectGroup']
          .get('unsubscribeFromAllThirdParty')
          .setValue(false);
      } else {
        this.communicationForm.controls['thirdPartySubscribeOptionSelectGroup']
          .get('subscribeToAllThirdParty')
          .setValue(false);
      }
    }

    if (!this.communicationForm.controls['thirdPartySubscribeOptionSelectGroup'].errors) {
      this.validationErrorMessage = null;
    } else {
      this.validationErrorMessage = 'Choose your marketing preferences to register!';
    }
  }

  public onPostCodeCheckChanges(inputEvent) {
    this._postcodeCheckChanged.next(inputEvent.target.value);
  }

  public onSelectAddressChanged(selectedAddress: AddressPAF) {
    this.addressModel = {
      addressLine1: this.createAddressLine1(selectedAddress),
      addressLine2: this.addressModel.addressLine2,
      townPerCity: selectedAddress.town,
      postcode: selectedAddress.postcode,
    };
  }

  public getAddressLabel(addressOption: AddressPAF) {
    return this.createAddressLine1(addressOption);
  }

  private initSubs() {
    this._subs.push(
      this._postcodeCheckChanged
        .pipe(debounceTime(1000))
        .subscribe((postcode: string) => {
          this.isPostcodeInvalid = false;
          this.verifyPostcodeAndGetAddressList(postcode);
        })
    );
  }

  private verifyPostcodeAndGetAddressList(postCode: string) {
    this._addressService
      .checkPostcodeValidity(postCode)
      .subscribe((isValidPostcode: boolean) => {
        this.addressesSelectList = [];
        if (isValidPostcode) {
          this._addressService
            .getAddressMatches(postCode)
            .subscribe((addresses: AddressPAF[]) => {
              this.addressesSelectList = addresses;
            });
        } else {
          this.isPostcodeInvalid = true;
        }
      });
  }

  private createAddressLine1(selectedAddress: AddressPAF): string {
    let result = `${selectedAddress.thoroughfare}`;
    if (selectedAddress.buildingNumber && selectedAddress.buildingNumber.length > 0) {
      result = `${selectedAddress.buildingNumber}, ` + result;
    }

    if (selectedAddress.buildingName && selectedAddress.buildingName.length > 0) {
      result = `${selectedAddress.buildingName}, ` + result;
    }

    if (selectedAddress.subBuildingName && selectedAddress.subBuildingName.length > 0) {
      result = `${selectedAddress.subBuildingName}, ` + result;
    }

    if (selectedAddress.organisationName && selectedAddress.organisationName.length > 0) {
      result = `${selectedAddress.organisationName}, ` + result;
    }

    return result;
  }
  private redirectToDashboard() {
    !this.user.tokRequests.length && this.router.navigate(['/dashboard']);
  }

  public checkValue(checked: boolean) {
    this.isPetPlanCheckboxChecked = checked;
  }
}
